import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Entra ID type-ahead picker for the Invite-User modal.
 *
 * Used only when User type = Internal (Styxtech staff) — those people
 * live in your Microsoft Entra tenant. External users (Customer /
 * Vendor) keep the plain email input because they aren't in Entra.
 *
 * UX: starts firing after 2 chars; debounced 250 ms; max 10 results.
 * Picking a result fills both email and display name in the parent
 * form. If Entra search returns nothing (or the User.Read.All
 * permission hasn't been granted to the App Service MI yet), the
 * dropdown shows a hint and the admin can still type the email
 * manually — the invite endpoint accepts it either way.
 */
import { useQuery } from '@tanstack/react-query';
import { t as tr } from '@/lib/i18n';
import { User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
export function EntraUserPicker({ email, onEmailChange, onPick, }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const [debouncedQuery, setDebouncedQuery] = useState('');
    // Debounce the typed value so we don't hammer Graph on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(email.trim()), 250);
        return () => clearTimeout(t);
    }, [email]);
    // Close on click outside.
    useEffect(() => {
        if (!open)
            return;
        const onDoc = (e) => {
            if (!wrapRef.current?.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);
    const showDropdown = open && debouncedQuery.length >= 2;
    const search = useQuery({
        queryKey: ['entra-search', debouncedQuery],
        queryFn: () => api.get(`/admin/users/entra-search?q=${encodeURIComponent(debouncedQuery)}&limit=10`),
        enabled: showDropdown,
        staleTime: 30_000,
    });
    return (_jsxs("div", { ref: wrapRef, className: "relative", children: [_jsx(Input, { type: "email", value: email, onChange: (e) => {
                    onEmailChange(e.target.value);
                    if (!open)
                        setOpen(true);
                }, onFocus: () => setOpen(true), placeholder: tr('Type to search Entra ID directory…'), required: true, autoFocus: true, autoComplete: "off" }), showDropdown && (_jsxs("div", { className: "absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900", children: [search.isLoading && (_jsx("div", { className: "px-3 py-3 text-xs text-slate-500", children: "Searching directory\u2026" })), !search.isLoading && (search.data?.length ?? 0) === 0 && (_jsx("div", { className: "px-3 py-3 text-xs text-slate-500", children: "No matches in your Entra tenant. You can still type the email manually if the person is being onboarded later." })), _jsx("ul", { className: "divide-y divide-slate-100 dark:divide-slate-800", children: (search.data ?? []).map((u) => (_jsx("li", { children: _jsx("button", { type: "button", onClick: () => {
                                    onPick(u);
                                    setOpen(false);
                                }, className: "block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(User, { size: 14, className: "shrink-0 text-slate-400" }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("div", { className: "truncate text-sm font-medium text-slate-800 dark:text-slate-200", children: u.display_name }), _jsx("div", { className: "truncate text-xs text-slate-500", children: u.email })] })] }) }) }, u.entra_object_id))) })] }))] }));
}
